/**
 * ADR 023 (BL-645) — `deriveAuctionPhaseCoarse`: the auction phase derivation
 * for realtime CLIENT consumers, which hold the auction's own decision stamps
 * (the `auction_snapshot` projection) but not its lots.
 *
 * **Since BL-1516 this IS `deriveAuctionPhaseKind`.** The "coarse" ladder was
 * only ever defined as "the full ladder minus the lot-dependent `post-sale`
 * rung", and that rung is retired — so the two derivations are the same
 * function over the same six stamps. Keeping a second copy of the ladder here
 * would be a duplicate with nothing left to distinguish it, and the two have
 * drifted before (`accepting-pre-bids` had to be added to both in lockstep), so
 * this module re-exports the canonical implementation instead.
 *
 * Collapsing the two NAMES — `deriveAuctionPhaseCoarse` /
 * `AuctionCoarsePhaseKind` / `AuctionPhaseCoarseInput` are used across
 * bidder-web, core-bidder and the realtime package — is a mechanical rename of
 * ~16 files and is declared **BL-1516 consolidation residue**, tracked as its
 * own item rather than bolted onto a lifecycle removal.
 *
 * Precedence (first match wins), unchanged:
 *   1 deleted            ← deletedAt
 *   2 closed             ← closedAt
 *   3 draft              ← readiedAt IS NULL
 *   4 accepting-pre-bids ← openedAt IS NULL AND preBiddingAt IS NOT NULL
 *   5 ready              ← openedAt IS NULL
 *   6 paused             ← pausedAt IS NOT NULL
 *   7 active             ← otherwise
 *
 * (BL-717: the `closing` rung was retired alongside `auctions.closing_started_at`
 * — see phase-tokens.ts for the full rationale.)
 */

import { deriveAuctionPhaseKind, type AuctionPhaseKindInput } from './derive-auction-phase-kind';
import type { AuctionPhaseKind } from './phase-tokens';

/** The coarse auction phase tokens. Identical to {@link AuctionPhaseKind} since
 *  BL-1516 retired the one rung that used to distinguish them. */
export type AuctionCoarsePhaseKind = AuctionPhaseKind;

/** The auction-snapshot decision stamps the predicate reads. Identical to
 *  {@link AuctionPhaseKindInput} since BL-1516. */
export type AuctionPhaseCoarseInput = AuctionPhaseKindInput;

/**
 * Derive an auction phase from its decision stamps.
 *
 * @param input The auction's (or `auction_snapshot`'s) decision-stamp fields.
 * @returns The {@link AuctionCoarsePhaseKind} (first matching rung wins).
 * @sideEffect none — pure.
 */
export const deriveAuctionPhaseCoarse: (input: AuctionPhaseCoarseInput) => AuctionCoarsePhaseKind =
  deriveAuctionPhaseKind;
